import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useActions, useSelector } from 'statux'

import InputMask from '@mona-health/react-input-mask'
import Form from '../Form/Form'
import FounderTracRegistration from '../FounderTracRegistration/FounderTracRegistration'

import authProvider from '../../utils/auth-provider'
import { getCssVar } from '../../utils/css'
import { getQueryParams } from '../../utils/location'
import { graphqlClient, gql } from '../../utils/gql'
import { countries, usStates, caTerritories } from '../../utils/geo'
import RichText from '../RichText/RichText'

export const ADVANCED_MEMBERSHIP_CHECKOUT_STEPS = [
    'onboard',
    'payment',
    'status'
]

export default function AdvancedMembershipCheckout (props = {
    onMutation: null,
    onStep: null,
    profile: null,
    step: null
}) {
    const setModal = useActions('modal')

    const setAdvancedMembershipAt = useActions('advancedMembershipAt')

    const {
        advancedMembershipPlan,
        advancedMembershipPrice
    } = useSelector()

    const role = authProvider.getDefaultRole()

    if (role !== 'member'
            || !props.profile
            || !props.profile.email
            || !advancedMembershipPlan?.id
            || !advancedMembershipPrice)
        return setModal(false)

    const location = useLocation()

    const queryParams = getQueryParams()

    const [paymentData, setPaymentData] = useState(null)

    const [paymentMutation, setPaymentMutation] = useState(null)

    const [step, setStep] = useState(props.step
            && ADVANCED_MEMBERSHIP_CHECKOUT_STEPS.indexOf(props.step) >= 0
            && (props.step !== 'status'
                || paymentMutation?.status)
        ? props.step
        : (props.step === 'status'
            ? 'payment'
            : ADVANCED_MEMBERSHIP_CHECKOUT_STEPS[0]))

    const [country, setCountry] = useState(null)

    useEffect(() => {
        const modalDialog = document.querySelector('#modal .modal-dialog')

        if (modalDialog?.scrollTop)
            modalDialog.scrollTo(0, 0)

        if (props.onStep
                && typeof props.onStep === 'function')
            props.onStep(step)
    }, [step])

    useEffect(() => {
        let queryStep = queryParams['advanced-membership-checkout']

        if (!queryStep
                || queryStep === step)
            return

        if (queryStep === 'status'
                && !paymentMutation?.status)
            queryStep = 'payment'

        if (ADVANCED_MEMBERSHIP_CHECKOUT_STEPS.indexOf(queryStep) === -1)
            queryStep = ADVANCED_MEMBERSHIP_CHECKOUT_STEPS[0]

        setStep(queryStep)
    }, [location.search])

    const onSubmit = async (e) => {
        const formData = new window.FormData(e.target)

        const data = Object.fromEntries(formData.entries())

        const expiration = (data.paymentInformation_card_expiration || '')
            .match(/(\d{1,2})\D(\d{4})/)

        data.paymentInformation_card_expirationMonth = expiration[1]

        data.paymentInformation_card_expirationYear = expiration[2]

        delete data.paymentInformation_card_expiration

        const payload = gql`{
            ${Object.keys(data)
                .map(key => gql`${key}: "${data[key]}"`)
                .join(',')}
        }`

        const mutation = await graphqlClient
            .request(gql`
                mutation {
                    create_payment_to_plan (
                        payload: ${payload}
                    ) {
                        authorizedAmount
                        errorMessage
                        bin
                        cardScheme
                        cardType
                        country
                        id
                        status
                        currency
                        issuer
                    }
                }
            `)
            .then(obj => obj.create_payment_to_plan)
            .catch(err => {
                console.warn(err)
                return {
                    errorMessage: 'An unknown error occurred. Please try again later'
                }
            })

        if (props.onMutation
                && typeof props.onMutation === 'function')
            props.onMutation(mutation)

        setPaymentMutation(mutation)

        if (mutation?.status === 'AUTHORIZED')
            setAdvancedMembershipAt((new Date()).toISOString())

        setStep('status')
    }

    const renderOnboard = () => (
        <>
            <div className="margin-3-b">
                <span
                    className="body-4 pad-1-t pad-1-b pad-2-r pad-2-l display-inline-flex"
                    style={{
                        borderRadius: 1000,
                        background: 'var(--aqua)',
                        color: 'var(--navy)',
                        fontWeight: 400
                    }}
                >
                    Enjoy lifetime access with a one time payment of {advancedMembershipPrice}
                </span>
            </div>

            <h2
                id="modal-heading"
                className="margin-1-b"
            >
                Accelerate your venture’s mission with Advanced Membership
            </h2>
            <p
                style={{
                    color: 'var(--navy)',
                    fontSize: '1rem'
                }}
            >
                Assessment. Goal-setting. Advising.
            </p>
            <hr
                className="margin-3-t margin-3-b"
                style={{
                    color: 'var(--geyser)'
                }}
            />
            <div
                className="rich-text text-align-left body-3"
                style={{
                    color: 'var(--navy)',
                    paddingBottom: 25
                }}
            >
                <h3 className="body-3">
                    <b>What is Advanced Membership?</b>
                </h3>
                <p>
                    We're excited to offer affordable, effective Advanced
                    Membership to Catapult members through our partnership with
                    FounderTrac. You’ll have access to Catapult’s Mentorship Hub,
                    featuring needs assessment, milestone mapping, and advisors
                    within a virtual environment that's helped over 400 ventures
                    advance their mission.
                </p>
                <p>Advanced Membership is a lifetime benefit, with a one-time fee of {advancedMembershipPrice}.</p>
                <p><strong>Gain access to:</strong></p>
                <ul>
                    <li>
                        <strong>Founder Focus Assessment: </strong>
                        Take our algorithmic intake survey to identify areas
                        your venture needs focus and support
                    </li>
                    <li>
                        <strong>Founder Focus Map: </strong>
                        Engage your needs and chart your progress across four
                        lanes of acceleration – Leadership, Product, Market,
                        and Finance
                    </li>
                    <li>
                        <strong>Session notes: </strong>
                        Document takeaways from advising sessions and
                        collaborate with your advisors
                    </li>
                    <li>
                        <strong>Advisor Matching: </strong>
                        Get paired with the right mentor for your specific needs
                    </li>
                    <li>
                        <strong>Waypoint completion: </strong>
                        Submit individual milestones for review and approval,
                        and gain confidence in your progress
                    </li>
                </ul>
            </div>
            <div
                className="modal-footer modal-footer-sticky advanced-membership-checkout-footer display-flex flex-direction-column pad-1-t pad-1-b pad-5-l pad-5-r"
                style={{
                    borderTop: '1px solid var(--geyser)',
                    background: 'var(--haze)',
                    margin: '0 -50px -50px'
                }}
            >
                <button
                    className="btn btn-lg btn-secondary margin-1-b"
                    onClick={() => {
                        setStep('payment')
                    }}
                >
                    <svg
                        className="icon-lock"
                        width="18"
                        height="18"
                    >
                        <use xlinkHref="#icon-lock" />
                    </svg>
                    <span>Continue to payment</span>
                </button>
                <RichText
                    className="body-5 margin-1-b"
                >
                    <p>Not sure? Please email our team at <a href="mailto:catapult@gse.upenn.edu">catapult@gse.upenn.edu</a></p>
                </RichText>
                <ul className="row flex-justify-content-center margin-1-b">
                    {['amex', 'mastercard', 'visa']
                        .map(str => (
                            <li
                                key={`payment-icons-${str}`}
                                className="col-auto"
                                style={{
                                    paddingLeft: 3,
                                    paddingRight: 3
                                }}
                            >
                                <svg
                                    aria-label={`${str
                                        .substring(0, 1)
                                        .toUpperCase()}${str.substring(1)}`}
                                    height="24"
                                    width="35"
                                    style={{
                                        borderRadius: str !== 'amex'
                                            && 4,
                                        border: str !== 'amex'
                                            && '1px solid var(--navy)'
                                    }}
                                >
                                    <use xlinkHref={`#icon-${str}`} />
                                </svg>
                            </li>
                        ))}
                </ul>
                <p className="body-5">
                    By purchasing Advanced Membership, you agree to allow Catapult
                    to share your data with FounderTrac. <Link to="/advanced-membership-terms">See terms for details.</Link>
                </p>
            </div>
        </>
    )

    const renderPayment = (
        isAuthorized = paymentMutation?.status === 'AUTHORIZED'
    ) => (
        <div
            className="row flex-justify-content-center"
        >
            <div className="col-12 col-10-xl-up">
                <h2
                    id="modal-heading"
                    className="margin-1-b"
                >
                    Payment details
                </h2>
                <p
                    style={{
                        color: 'var(--navy)',
                        fontSize: '1rem'
                    }}
                >
                    Enter your payment details below to activate your Advanced Membership.
                </p>
                <hr
                    className="margin-3-t margin-3-b"
                    style={{
                        color: 'var(--geyser)'
                    }}
                />
                <Form
                    id="advanced-membership-checkout-form"
                    className="text-align-left"
                    disabled={isAuthorized}
                    onSubmit={onSubmit}
                    onStateUpdate={(e) => {
                        let state

                        try {
                            state = JSON.parse(e.newValue)
                        }
                        catch (e) {}

                        setPaymentData(state || null)
                    }}
                    enableOnChange
                >
                    <input
                        type="hidden"
                        name="orderInformation_billTo_email"
                        value={props.profile.email}
                    />
                    <input
                        type="hidden"
                        name="planId"
                        value={advancedMembershipPlan.id}
                    />
                    <fieldset className="row-condensed">
                        <div className="col-12 margin-1-b">
                            <legend>
                                <b>Card details</b>
                            </legend>
                        </div>
                        <div className="col-12 col-6-lg-up margin-b-lg-down">
                            <div className="form-field-floating">
                                <input
                                    id="paymentInformation_card_number"
                                    name="paymentInformation_card_number"
                                    defaultValue={paymentData?.paymentInformation_card_number}
                                    disabled={isAuthorized}
                                    type="number"
                                    autoComplete="cc-number"
                                    spellCheck="false"
                                    minLength="1"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="paymentInformation_card_number"
                                >
                                    Card number
                                </label>

                                <p
                                    id="paymentInformation_card_number-invalid"
                                    role="alert"
                                >
                                    A credit card number is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 col-3-lg-up margin-1-b-lg-down">
                            <div className="form-field-floating">
                                <InputMask
                                    id="paymentInformation_card_expiration"
                                    name="paymentInformation_card_expiration"
                                    defaultValue={paymentData?.paymentInformation_card_expiration}
                                    disabled={isAuthorized}
                                    autoComplete="cc-exp"
                                    spellCheck="false"
                                    pattern="^\d{1,2}(?:\/|-)\d{4}$"
                                    mask="99/9999"
                                    maskPlaceholder={null}
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="paymentInformation_card_expiration"
                                >
                                    Expiration<span className="display-none-lg"> date</span>
                                </label>
                                <span
                                    className="body-5 display-block margin-1-t text-align-left"
                                >
                                    MM/YYYY
                                </span>
                                <p
                                    id="paymentInformation_card_expiration-invalid"
                                    role="alert"
                                >
                                    An expiration date is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 col-3-lg-up margin-b">
                            <div className="form-field-floating">
                                <input
                                    id="paymentInformation_card_securityCode"
                                    name="paymentInformation_card_securityCode"
                                    disabled={isAuthorized}
                                    type="number"
                                    autoComplete="cc-csc"
                                    spellCheck="false"
                                    minLength="1"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="paymentInformation_card_securityCode"
                                >
                                    Security code
                                </label>
                                <p
                                    id="paymentInformation_expiration-invalid"
                                    role="alert"
                                >
                                    A security code is required
                                </p>
                            </div>
                        </div>
                    </fieldset>
                    <fieldset className="row-condensed">
                        <div className="col-12 margin-1-b">
                            <legend>
                                <b>Billing details</b>
                            </legend>
                        </div>
                        <div className="col-12 col-6-lg-up margin-b-lg-down">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_firstName"
                                    name="orderInformation_billTo_firstName"
                                    defaultValue={paymentData?.orderInformation_billTo_firstName}
                                    disabled={isAuthorized}
                                    autoComplete="cc-given-name"
                                    spellCheck="false"
                                    pattern=".{2,}$"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="orderInformation_billTo_firstName"
                                >
                                    First name
                                </label>

                                <p
                                    id="paymentInformation_card_number-invalid"
                                    role="alert"
                                >
                                    A first name is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 col-6-lg-up margin-b">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_lastName"
                                    name="orderInformation_billTo_lastName"
                                    defaultValue={paymentData?.orderInformation_billTo_lastName}
                                    disabled={isAuthorized}
                                    autoComplete="cc-family-name"
                                    spellCheck="false"
                                    pattern=".+"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="orderInformation_billTo_lastName"
                                >
                                    Last name
                                </label>

                                <p
                                    id="orderInformation_billTo_lastName-invalid"
                                    role="alert"
                                >
                                    A last name is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 margin-b">
                            <div className="form-field">
                                <select
                                    id="orderInformation_billTo_country"
                                    name="orderInformation_billTo_country"
                                    defaultValue={paymentData?.orderInformation_billTo_country}
                                    disabled={isAuthorized}
                                    autoComplete="country"
                                    onChange={(e) => setCountry(e.target.value)}
                                    required
                                >
                                    <option value="">Country</option>
                                    {Object.keys(countries)
                                        .map(key => (
                                            <option
                                                key={`country-${key}`}
                                                value={key}
                                            >
                                                {countries[key]}
                                            </option>
                                        ))}
                                </select>

                                <p
                                    id="orderInformation_billTo_country-invalid"
                                    role="alert"
                                >
                                    A country is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 margin-b">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_address1"
                                    name="orderInformation_billTo_address1"
                                    defaultValue={paymentData?.orderInformation_billTo_address1}
                                    disabled={isAuthorized}
                                    autoComplete="address-line1"
                                    spellCheck="false"
                                    pattern=".+"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="orderInformation_billTo_address1"
                                >
                                    Address line 1
                                </label>

                                <p
                                    id="orderInformation_billTo_address1-invalid"
                                    role="alert"
                                >
                                    An address is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 margin-b">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_address2"
                                    name="orderInformation_billTo_address2"
                                    defaultValue={paymentData?.orderInformation_billTo_address2}
                                    disabled={isAuthorized}
                                    autoComplete="address-line2"
                                    spellCheck="false"
                                    placeholder=" "
                                />
                                <label
                                    htmlFor="orderInformation_billTo_address2"
                                >
                                    Address line 2 (Optional)
                                </label>
                                <span
                                    className="body-5 display-block margin-1-t text-align-left"
                                >
                                    Apartment, suite, etc
                                </span>
                            </div>
                        </div>
                        <div className="col-12 col-6-lg-up margin-b-lg-down">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_locality"
                                    name="orderInformation_billTo_locality"
                                    defaultValue={paymentData?.orderInformation_billTo_locality}
                                    disabled={isAuthorized}
                                    autoComplete="address-level2"
                                    spellCheck="false"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="orderInformation_billTo_locality"
                                >
                                    City
                                </label>

                                <p
                                    id="orderInformation_billTo_locality-invalid"
                                    role="alert"
                                >
                                    City is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 col-3-lg-up margin-b">
                            <div className="form-field-floating">
                                <input
                                    id="orderInformation_billTo_postalCode"
                                    name="orderInformation_billTo_postalCode"
                                    defaultValue={paymentData?.orderInformation_billTo_postalCode}
                                    disabled={isAuthorized}
                                    type={country === 'US'
                                        ? 'number'
                                        : 'text'}
                                    autoComplete="postal-code"
                                    spellCheck="false"
                                    minLength="1"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    htmlFor="orderInformation_billTo_postalCode"
                                >
                                    Postal code
                                </label>
                                <p
                                    id="orderInformation_billTo_postalCode-invalid"
                                    role="alert"
                                >
                                    Postal code is required
                                </p>
                            </div>
                        </div>
                        <div className="col-12 col-3-lg-up margin-b">
                            <div
                                className={`form-field${
                                    country !== 'US'
                                        ? ' display-none'
                                        : ''}`}
                            >
                                <select
                                    id="orderInformation_billTo_usStates"
                                    name={country === 'US'
                                        ? 'orderInformation_billTo_administrativeArea'
                                        : null}
                                    defaultValue={country === 'US'
                                        && paymentData?.orderInformation_billTo_administrativeArea}
                                    disabled={isAuthorized}
                                    autoComplete="address-level1"
                                    required={country === 'US'}
                                >
                                    <option value="">State</option>
                                    {Object.keys(usStates)
                                        .map(key => (
                                            <option
                                                key={`us-state-${key}`}
                                                value={key}
                                            >
                                                {usStates[key]}
                                            </option>
                                        ))}
                                </select>
                                <p
                                    id="orderInformation_billTo_usStates-invalid"
                                    role="alert"
                                >
                                    State is required
                                </p>
                            </div>

                            <div
                                className={`form-field${
                                    country !== 'CA'
                                        ? ' display-none'
                                        : ''}`}
                            >
                                <select
                                    id="orderInformation_billTo_caTerritories"
                                    name={country === 'CA'
                                        ? 'orderInformation_billTo_administrativeArea'
                                        : null}
                                    defaultValue={country === 'CA'
                                        && paymentData?.orderInformation_billTo_administrativeArea}
                                    disabled={isAuthorized}
                                    autoComplete="address-level1"
                                    required={country === 'CA'}
                                >
                                    <option value="">Territory</option>
                                    {Object.keys(caTerritories)
                                        .map(key => (
                                            <option
                                                key={`ca-territory-${key}`}
                                                value={key}
                                            >
                                                {caTerritories[key]}
                                            </option>
                                        ))}
                                </select>
                                <p
                                    id="orderInformation_billTo_caTerritores-invalid"
                                    role="alert"
                                >
                                    Territory is required
                                </p>
                            </div>

                            <div
                                className={`form-field-floating${
                                    country === 'US'
                                            || country === 'CA'
                                        ? ' display-none'
                                        : ''}`}
                            >
                                <input
                                    id="orderInformation_billTo_administrativeArea"
                                    name={country !== 'US'
                                            && country !== 'CA'
                                        ? 'orderInformation_billTo_administrativeArea'
                                        : null}
                                    defaultValue={country !== 'US'
                                        && country !== 'CA'
                                        && paymentData?.orderInformation_billTo_administrativeArea}
                                    disabled={isAuthorized}
                                    autoComplete="address-level1"
                                    spellCheck="false"
                                    placeholder=" "
                                    required={country !== 'US'
                                        && country !== 'CA'}
                                />
                                <label
                                    htmlFor="orderInformation_billTo_administrativeArea"
                                >
                                    State
                                </label>
                                <p
                                    id="orderInformation_billTo_administrativeArea-invalid"
                                    role="alert"
                                >
                                    State is required
                                </p>
                            </div>
                        </div>
                    </fieldset>
                    <div className="display-flex">
                        <button
                            className="btn btn-lg btn-secondary flex-grow"
                            type="submit"
                            disabled={paymentData === null}
                        >
                            <svg
                                className="icon-lock"
                                width="18"
                                height="18"
                            >
                                <use xlinkHref="#icon-lock" />
                            </svg>
                            <span>Complete payment</span>
                        </button>
                    </div>
                </Form>
            </div>
        </div>
    )

    const renderStatus = () => {
        const isAuthorized = paymentMutation?.status === 'AUTHORIZED'

        const heading = isAuthorized
            ? 'Payment successful!'
            : 'Payment unsuccessful'

        const text = isAuthorized
            ? 'Congratulations, you’re now an Advanced Member. Save the information below for your records.'
            : 'We weren’t able to process your payment. Please verify your information is correct and try again.'

        const currency = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: paymentMutation.currency || 'USD'
        })

        return (
            <div
                className="row flex-justify-content-center"
                style={{
                    color: 'var(--navy)',
                    fontSize: '1rem'
                }}
            >
                <div className="col-12 col-10-lg-up">
                    <h2
                        id="modal-heading"
                        className="margin-1-b"
                    >
                        {heading}
                    </h2>
                    <p
                        style={{
                            fontSize: '1rem'
                        }}
                    >
                        {text}
                    </p>
                    <hr
                        className="margin-3-t margin-3-b"
                        style={{
                            color: 'var(--geyser)'
                        }}
                    />
                    <div className="text-align-left">
                        <p className="margin-b">
                            <strong className="margin-1-r">Total payment</strong>
                            <strong>
                                {paymentMutation.currency} {currency
                                    .format(paymentMutation.authorizedAmount || 0)}
                            </strong>
                        </p>
                        {(paymentMutation?.id
                                || paymentMutation?.cardScheme
                                || paymentMutation?.issuer)
                            && (
                                <ul className="row-condensed">
                                    {paymentMutation.id
                                        && (
                                            <li className="col-12 col-6-md-up margin-3-b">
                                                <div className="form-field-floating">
                                                    <input
                                                        id="paymentMutation_refNumber"
                                                        name="paymentMutation_refNumber"
                                                        spellCheck="false"
                                                        placeholder=" "
                                                        value={paymentMutation.id}
                                                        readOnly
                                                    />
                                                    <label
                                                        htmlFor="paymentMutation_refNumber"
                                                    >
                                                        Ref number
                                                    </label>
                                                </div>
                                            </li>)}
                                    <li className="col-12 col-6-md-up margin-3-b">
                                        <div className="form-field-floating">
                                            <input
                                                id="paymentMutation_time"
                                                name="paymentMutation_time"
                                                spellCheck="false"
                                                placeholder=" "
                                                value={new Date().toISOString()}
                                                readOnly
                                            />
                                            <label
                                                htmlFor="paymentMutation_time"
                                            >
                                                Payment time
                                            </label>
                                        </div>
                                    </li>
                                    {paymentMutation.cardScheme
                                        && (
                                            <li className="col-12 col-6-md-up margin-3-b">
                                                <div className="form-field-floating">
                                                    <input
                                                        id="paymentMutation_cardScheme"
                                                        name="paymentMutation_cardScheme"
                                                        spellCheck="false"
                                                        placeholder=" "
                                                        value={paymentMutation.cardScheme}
                                                        readOnly
                                                    />
                                                    <label
                                                        htmlFor="paymentMutation_cardScheme"
                                                    >
                                                        Payment method
                                                    </label>
                                                </div>
                                            </li>)}
                                    {paymentMutation.issuer
                                        && (
                                            <li className="col-12 col-6-md-up margin-3-b">
                                                <div className="form-field-floating">
                                                    <input
                                                        id="paymentMutation_issuer"
                                                        name="paymentMutation_issuer"
                                                        spellCheck="false"
                                                        placeholder=" "
                                                        value={paymentMutation.issuer}
                                                        readOnly
                                                    />
                                                    <label
                                                        htmlFor="paymentMutation_issuer"
                                                    >
                                                        Issuer
                                                    </label>
                                                </div>
                                            </li>)}
                                </ul>)}
                        {isAuthorized
                            && (
                                <>
                                    <div
                                        className="rich-text"
                                        style={{
                                            color: 'var(--navy)'
                                        }}
                                    >
                                        <p><strong>Next step</strong></p>
                                        <p>
                                            Now that you’re an Advanced Member, you can access
                                            the <strong>Mentorship Hub from your Catapult Dashboard</strong>.
                                        </p>
                                        <p>
                                            Get started by completing
                                            your <strong>Founder Focus Assessment</strong> – an
                                            algorithmic intake survey that identifies areas your
                                            venture needs focus and support. Your responses are used
                                            to generate your <strong>Founder Focus Map</strong>, which
                                            provides you with actionable steps and a foundation
                                            for your advisor. You’ll receive a notification
                                            when your Founder Focus Map is ready.
                                        </p>
                                    </div>
                                    <div className="display-flex flex-direction-column margin-3-t">
                                        <button
                                            className="btn btn-lg btn-secondary flex-grow"
                                            type="button"
                                            onClick={() => setModal({
                                                className: 'modal-foundertrac-registration',
                                                'aria-labelledby': 'modal-heading',
                                                size: 'lg',
                                                dialogScrollable: true,
                                                icon: 'icon-like',
                                                iconBackground: getCssVar('--aqua'),
                                                component: <FounderTracRegistration profile={props.profile} />
                                            })}
                                        >
                                            <svg
                                                className="icon-route"
                                                width="18"
                                                height="18"
                                            >
                                                <use xlinkHref="#icon-route" />
                                            </svg>
                                            <span>Get started</span>
                                        </button>
                                        <p className="margin-1-t body-5 text-align-center">
                                            <span>Access the Membership Hub from your Dashboard at any time through the </span>
                                            <svg
                                                className="icon-route"
                                                width="18"
                                                height="18"
                                                style={{
                                                    display: 'inline',
                                                    transform: 'translateY(25%)'
                                                }}
                                            >
                                                <use xlinkHref="#icon-route" />
                                            </svg>
                                            <span> icon.</span>
                                        </p>
                                    </div>
                                </>)}
                        {!isAuthorized
                            && (
                                <div className="display-flex flex-direction-column">
                                    <button
                                        className="btn btn-lg btn-secondary flex-grow"
                                        type="button"
                                        onClick={() => setStep('payment')}
                                    >
                                        <span>Return to payment</span>
                                    </button>
                                </div>)}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`advanced-membership-checkout advanced-membership-checkout-step-${
                step}`}
        >
            {step === 'onboard'
                && renderOnboard()}
            {step === 'payment'
                && renderPayment()}
            {step === 'status'
                && renderStatus()}
        </div>
    )
}
